/**
 * MCP Prompts Panel - System Prompt Customization for MCP Tools
 *
 * Allows users to customize system prompts for the following MCP tools:
 * - basic_format (Befund Formatierung)
 * - autocorrect (Grammatik)
 * - ultrathink (Logikprüfung)
 *
 * Opens as an overlay panel with tabs for each tool.
 */

import { showConfirmation } from './modal.js';
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, placeholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { indentOnInput, syntaxHighlighting } from "@codemirror/language";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { Table } from "@lezer/markdown";
import richMarkdown from "codemirror-rich-markdoc";
import * as Markdoc from "@markdoc/markdoc";
import customHighlightStyle from "./highlightStyle.js";
import { obsidianAutoWrap } from "./editor-cm6.js";
import { codeBlockHighlighter } from "./codeblock-highlighter.js";
import { highlightHighlighter } from "./highlight-highlighter.js";
import { bracketHighlighter } from "./bracket-highlighter.js";
import { tagHighlighter } from "./tag-highlighter.js";
import { listContinuation, listAutoRenumber } from "./list-continuation.js";

// Tool descriptions for the UI
const TOOL_DESCRIPTIONS = {
    basic_format: {
        title: 'Befund Formatierung',
        desc: 'Strukturiert unformatierte Befunde in eine standardisierte Form mit Grundstruktur und chronologischer Ordnung. An der Stelle der Marke {{SKELETT}} setzt der Lauf das Skelett des gewählten Modus ein (Standard oder Vorlage) - die Marke muss im Text erhalten bleiben.'
    },
    autocorrect: {
        title: 'Grammatik & Rechtschreibung',
        desc: 'Korrigiert Rechtschreibung, Grammatik und medizinische Fachbegriffe unter Beibehaltung der Formatierung.'
    },
    ultrathink: {
        title: 'Logikprüfung',
        desc: 'Prüft den Befund auf logische Widersprüche und Inkonsistenzen zwischen Befund und Beurteilung.'
    }
};

export class McpPromptsPanel {
    constructor() {
        this.isOpen = false;
        this.panel = null;
        this.prompts = [];
        this.activeTab = 'basic_format';
        this.hasUnsavedChanges = false;
        this.editor = null;
    }

    /**
     * Initialize the panel
     */
    init() {
        this.panel = document.getElementById('mcp-prompts-panel');
        if (!this.panel) {
            return;
        }
        this.panel.innerHTML = this.getContent();
    }

    /**
     * Get the panel HTML content
     */
    getContent() {
        return `
            <div class="admin-content mcp-prompts-content">
                <div class="admin-header">
                    <h2>Editor Tools</h2>
                    <button id="mcp-prompts-close-btn" class="panel-btn panel-btn-sm" title="Schließen">✕</button>
                </div>

                <div class="mcp-prompts-tabs">
                    <button class="mcp-prompt-tab active" data-tool="basic_format" title="Befund Formatierung">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                    </button>
                    <button class="mcp-prompt-tab" data-tool="autocorrect" title="Grammatik">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </button>
                    <button class="mcp-prompt-tab" data-tool="ultrathink" title="Logikprüfung">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"></path>
                        </svg>
                    </button>
                </div>

                <div class="mcp-prompts-tool-description">
                    <div id="mcp-prompt-tool-title" class="mcp-prompt-tool-title">Befund Formatierung</div>
                    <div id="mcp-prompt-tool-desc" class="mcp-prompt-tool-desc">Strukturiert unformatierte Befunde in eine standardisierte Form mit Grundstruktur und chronologischer Ordnung.</div>
                </div>

                <div class="admin-body mcp-prompts-body">
                    <div id="mcp-prompt-editor" class="mcp-prompt-editor">
                        <div class="mcp-prompt-header">
                            <span class="mcp-prompt-header-label">Gib dem Tool hier deine Anweisungen</span>
                            <span id="mcp-prompt-status" class="mcp-prompt-status">Default</span>
                        </div>
                        <div class="mcp-prompt-textarea-wrapper">
                            <div id="mcp-prompt-codemirror" class="mcp-prompt-codemirror"></div>
                            <div id="mcp-prompt-message" class="mcp-prompt-message"></div>
                            <div id="mcp-prompt-confirm-strip" class="mcp-prompt-confirm-strip" style="display: none;">
                                <span id="mcp-prompt-confirm-text">Prompt wirklich auf Default zurücksetzen?</span>
                                <button id="mcp-prompt-confirm-yes" class="form-btn form-btn-primary">Bestätigen</button>
                                <button id="mcp-prompt-confirm-no" class="form-btn form-btn-secondary">Abbrechen</button>
                            </div>
                        </div>
                        <div class="mcp-prompt-actions">
                            <button id="mcp-prompt-save" class="form-btn form-btn-primary">Speichern</button>
                            <button id="mcp-prompt-reset" class="form-btn form-btn-secondary">Reset auf Default</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
        // Close button
        const closeBtn = document.getElementById('mcp-prompts-close-btn');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
        }

        // Tab switching - use currentTarget to get the button, not the SVG inside
        document.querySelectorAll('.mcp-prompt-tab').forEach(tab => {
            tab.addEventListener('click', (e) => {
                const tool = e.currentTarget.dataset.tool;
                this.switchTab(tool);
            });
        });

        // Save button
        const saveBtn = document.getElementById('mcp-prompt-save');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => this.savePrompt());
        }

        // Reset button - shows confirmation strip
        const resetBtn = document.getElementById('mcp-prompt-reset');
        if (resetBtn) {
            resetBtn.addEventListener('click', () => this.showResetConfirmation());
        }

        // Confirmation strip buttons
        const confirmYesBtn = document.getElementById('mcp-prompt-confirm-yes');
        if (confirmYesBtn) {
            confirmYesBtn.addEventListener('click', () => this.confirmReset());
        }

        const confirmNoBtn = document.getElementById('mcp-prompt-confirm-no');
        if (confirmNoBtn) {
            confirmNoBtn.addEventListener('click', () => this.hideResetConfirmation());
        }
    }

    /**
     * Initialize CodeMirror editor with Obsidian-like markdown editing
     */
    initEditor() {
        const container = document.getElementById('mcp-prompt-codemirror');
        if (!container) return;

        // Destroy existing editor if any
        if (this.editor) {
            this.editor.destroy();
        }

        // Markdoc configuration for rich markdown rendering
        const markdocConfig = {
            tags: {},
            nodes: {},
            functions: {},
            variables: {}
        };

        // Create CodeMirror editor with full markdown support
        this.editor = new EditorView({
            state: EditorState.create({
                doc: '',
                extensions: [
                    history(),
                    indentOnInput(),
                    syntaxHighlighting(customHighlightStyle),
                    // Markdown language support
                    markdown({
                        base: markdownLanguage,
                        codeLanguages: languages,
                        extensions: [Table]
                    }),
                    // Rich markdown WYSIWYG rendering (bold, italic, headings, etc.)
                    richMarkdown({
                        markdoc: markdocConfig,
                        lezer: {
                            codeLanguages: languages,
                            extensions: [Table]
                        }
                    }),
                    // Obsidian-like auto-wrapping (select text + * = bold, etc.)
                    obsidianAutoWrap,
                    // Code block background highlighting
                    codeBlockHighlighter,
                    // Highlight/mark background (==text== syntax)
                    highlightHighlighter,
                    // Bracket/purple highlight ([text] syntax)
                    bracketHighlighter,
                    // Tag highlight (#tag syntax - Obsidian-style blue)
                    tagHighlighter,
                    // Numbered list continuation and auto-renumbering
                    listContinuation,
                    listAutoRenumber,
                    keymap.of([
                        indentWithTab,
                        ...defaultKeymap,
                        ...historyKeymap
                    ]),
                    placeholder('Anweisungen eingeben...'),
                    EditorView.lineWrapping,
                    EditorView.updateListener.of((update) => {
                        if (update.docChanged) {
                            this.hasUnsavedChanges = true;
                            this.updateSaveButtonState();
                        }
                    }),
                    // Dark theme matching the main editor
                    EditorView.theme({
                        "&": {
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            color: "#fff",
                            fontSize: "15px",
                            lineHeight: "1.6",
                            fontFamily: "'Fira Code', monospace"
                        },
                        ".cm-content": {
                            caretColor: "#fff",
                            padding: "12px"
                        },
                        ".cm-cursor": {
                            borderLeftColor: "#fff"
                        },
                        "&.cm-focused .cm-selectionBackground, .cm-selectionBackground": {
                            backgroundColor: "rgba(74, 158, 255, 0.3)"
                        },
                        ".cm-activeLine": {
                            backgroundColor: "rgba(255, 255, 255, 0.05)"
                        },
                        ".cm-gutters": {
                            display: "none"
                        },
                        ".cm-placeholder": {
                            color: "rgba(255, 255, 255, 0.3)"
                        }
                    }, { dark: true })
                ]
            }),
            parent: container
        });
    }

    /**
     * Get editor content
     */
    getEditorContent() {
        if (this.editor) {
            return this.editor.state.doc.toString();
        }
        return '';
    }

    /**
     * Set editor content
     */
    setEditorContent(content) {
        if (this.editor) {
            this.editor.dispatch({
                changes: {
                    from: 0,
                    to: this.editor.state.doc.length,
                    insert: content
                }
            });
        }
    }

    /**
     * Open the panel as overlay
     */
    async openOverlay() {
        if (!this.panel) return;

        // Render content
        this.panel.innerHTML = this.getContent();

        this.isOpen = true;
        this.panel.classList.add('visible', 'overlay-mode');
        this.panel.classList.remove('split-mode', 'full-mode');

        // Attach event listeners and initialize editor
        setTimeout(async () => {
            this.attachEventListeners();
            this.initEditor();
            await this.loadPrompts();
        }, 100);
    }

    /**
     * Close the panel
     */
    async close() {
        if (!this.panel) return;

        // Check for unsaved changes
        if (this.hasUnsavedChanges) {
            const confirmed = await showConfirmation('Du hast ungespeicherte Änderungen. Wirklich schließen?', 'Ungespeicherte Änderungen');
            if (!confirmed) {
                return;
            }
        }

        this.isOpen = false;
        this.panel.classList.remove('visible', 'overlay-mode', 'split-mode', 'full-mode');
        this.hasUnsavedChanges = false;
        this.hideResetConfirmation();

        // Destroy editor
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
    }

    /**
     * Load all prompts from API
     */
    async loadPrompts() {
        try {
            const response = await fetch('/api/user/mcp-prompts', {
                headers: {
                }
            });

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Prompts');
            }

            const data = await response.json();
            this.prompts = data.prompts;

            // Display the first tab
            this.displayPrompt(this.activeTab);
        } catch (error) {
            this.showMessage('Fehler: ' + error.message, 'error');
        }
    }

    /**
     * Switch to a different tab
     */
    async switchTab(toolName) {
        // Check for unsaved changes
        if (this.hasUnsavedChanges) {
            const confirmed = await showConfirmation('Du hast ungespeicherte Änderungen. Tab wechseln?', 'Ungespeicherte Änderungen');
            if (!confirmed) {
                return;
            }
        }

        // Hide any open confirmation strip
        this.hideResetConfirmation();

        this.activeTab = toolName;
        this.hasUnsavedChanges = false;

        // Update tab buttons
        document.querySelectorAll('.mcp-prompt-tab').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tool === toolName);
        });

        // Display the prompt
        this.displayPrompt(toolName);
    }

    /**
     * Display a prompt in the editor
     */
    displayPrompt(toolName) {
        const prompt = this.prompts.find(p => p.tool_name === toolName);
        if (!prompt) {
            return;
        }

        // Update tool title and description
        const toolDesc = TOOL_DESCRIPTIONS[toolName];
        const toolTitleEl = document.getElementById('mcp-prompt-tool-title');
        const toolDescEl = document.getElementById('mcp-prompt-tool-desc');
        if (toolTitleEl && toolDesc) {
            toolTitleEl.textContent = toolDesc.title;
        }
        if (toolDescEl && toolDesc) {
            toolDescEl.textContent = toolDesc.desc;
        }

        // Update status
        const statusEl = document.getElementById('mcp-prompt-status');
        if (statusEl) {
            statusEl.textContent = prompt.is_custom ? 'Custom' : 'Default';
            statusEl.className = 'mcp-prompt-status ' + (prompt.is_custom ? 'custom' : 'default');
        }

        // Update editor content
        this.setEditorContent(prompt.system_prompt);

        // Reset unsaved changes flag (after setting content to avoid false positive)
        setTimeout(() => {
            this.hasUnsavedChanges = false;
            this.updateSaveButtonState();
        }, 10);
        this.clearMessage();
    }

    /**
     * Save the current prompt
     */
    async savePrompt() {
        const systemPrompt = this.getEditorContent().trim();
        if (!systemPrompt) {
            this.showMessage('Prompt darf nicht leer sein', 'error');
            return;
        }

        try {
            const response = await fetch(`/api/user/mcp-prompts/${this.activeTab}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ system_prompt: systemPrompt })
            });

            if (!response.ok) {
                throw new Error('Fehler beim Speichern');
            }
            const result = await response.json().catch(() => ({}));

            // Update local state
            const prompt = this.prompts.find(p => p.tool_name === this.activeTab);
            if (prompt) {
                prompt.system_prompt = systemPrompt;
                prompt.is_custom = true;
            }

            // Update status
            const statusEl = document.getElementById('mcp-prompt-status');
            if (statusEl) {
                statusEl.textContent = 'Custom';
                statusEl.className = 'mcp-prompt-status custom';
            }

            this.hasUnsavedChanges = false;
            this.updateSaveButtonState();
            if (result.warning) {
                // Gespeichert, aber mit Haken: bleibt stehen, bis der Tab
                // wechselt, statt nach drei Sekunden zu verschwinden.
                this.showMessage(result.warning, 'warning');
            } else {
                this.showMessage('Gespeichert!', 'success');
            }
        } catch (error) {
            this.showMessage('Fehler: ' + error.message, 'error');
        }
    }

    /**
     * Show reset confirmation strip
     */
    showResetConfirmation() {
        const strip = document.getElementById('mcp-prompt-confirm-strip');
        if (strip) {
            strip.style.display = 'flex';
        }
    }

    /**
     * Hide reset confirmation strip
     */
    hideResetConfirmation() {
        const strip = document.getElementById('mcp-prompt-confirm-strip');
        if (strip) {
            strip.style.display = 'none';
        }
    }

    /**
     * Reset the current prompt to default (called after confirmation)
     */
    async confirmReset() {
        this.hideResetConfirmation();

        try {
            const response = await fetch(`/api/user/mcp-prompts/${this.activeTab}`, {
                method: 'DELETE',
                headers: {
                }
            });

            if (!response.ok) {
                throw new Error('Fehler beim Zurücksetzen');
            }

            // Reload prompts to get default
            await this.loadPrompts();
            this.showMessage('Auf Default zurückgesetzt!', 'success');
        } catch (error) {
            this.showMessage('Fehler: ' + error.message, 'error');
        }
    }

    /**
     * Update save button state
     */
    updateSaveButtonState() {
        const saveBtn = document.getElementById('mcp-prompt-save');
        if (saveBtn) {
            saveBtn.disabled = !this.hasUnsavedChanges;
            saveBtn.classList.toggle('has-changes', this.hasUnsavedChanges);
        }
    }

    /**
     * Show a message
     */
    showMessage(text, type = 'info') {
        const messageEl = document.getElementById('mcp-prompt-message');
        if (messageEl) {
            messageEl.textContent = text;
            messageEl.className = 'mcp-prompt-message ' + type;
            messageEl.style.display = 'block';

            // Auto-hide after 3 seconds for success messages
            if (type === 'success') {
                setTimeout(() => this.clearMessage(), 3000);
            }
        }
    }

    /**
     * Clear the message
     */
    clearMessage() {
        const messageEl = document.getElementById('mcp-prompt-message');
        if (messageEl) {
            messageEl.textContent = '';
            messageEl.style.display = 'none';
        }
    }
}

// Initialize MCP Prompts Panel
let mcpPromptsPanelInstance = null;

export function initMcpPromptsPanel() {
    if (!mcpPromptsPanelInstance) {
        mcpPromptsPanelInstance = new McpPromptsPanel();
        mcpPromptsPanelInstance.init();
    }
    return mcpPromptsPanelInstance;
}

export function getMcpPromptsPanel() {
    return mcpPromptsPanelInstance;
}
